import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIsPresent } from 'framer-motion';

type Updater<T extends string> = T | ((prev: T) => T);

export function useUrlParam<T extends string = string>(
  key: string,
  defaultValue: T,
): [T, (next: Updater<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = (searchParams.get(key) ?? defaultValue) as T;
  const present = useIsPresent();

  const setValue = useCallback(
    (next: Updater<T>) => {
      if (!present) return;
      setSearchParams(
        (prev) => {
          const current = (prev.get(key) ?? defaultValue) as T;
          const resolved =
            typeof next === 'function' ? (next as (p: T) => T)(current) : next;
          const params = new URLSearchParams(prev);
          if (resolved === defaultValue || resolved === '') params.delete(key);
          else params.set(key, resolved);
          return params;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams, present],
  );

  return [value, setValue];
}

export function usePatchUrlParams(): (
  patch: Record<string, string | null | undefined>,
) => void {
  const [, setSearchParams] = useSearchParams();
  const present = useIsPresent();

  return useCallback(
    (patch: Record<string, string | null | undefined>) => {
      if (!present) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, next] of Object.entries(patch)) {
            if (next == null || next === '') params.delete(key);
            else params.set(key, next);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, present],
  );
}
